import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useLocation, useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { OverAllLoader } from "../../components/OverAllLoader";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";


const AddInstituteAdmin = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  // ==== states =======
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [overAllLoading, setOverAllLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [instituteNames, setInstituteNames] = useState([]);
  const { addAdmin, adminID } = location.state || "";

  const statuses = ["ACTIVE", "INACTIVE"];

  // ===== validation ========

  const schema = Yup.object().shape({
    firstName: Yup.string()
      .trim()
      .required("Please enter the first name")
      .matches(/^[A-Za-z]+(?:[ .'-]+[A-Za-z]+)*\.?$/,"First name can only contain letters")
      .min(2, "First name must be at least 2 characters")
      .max(50, "First name must not exceed 50 characters"),
    lastName: Yup.string()
      .required("Please enter the last name")
      .matches(/^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/,"Last name can only contain letters")
      .max(50, "Last name must not exceed 50 characters"),   
    contactNumber: Yup.string()
      .required("Please enter the contact number")
      .min(10, "Atleast 10 numbers is required")
      .max(15, "Contact number must not exceed 15 digits"),
    email: Yup.string()
      .required("Please enter your Email Address")
      .matches(
        /^(?=(?:[^@]*[A-Za-z]){2})[A-Za-z0-9][A-Za-z0-9._%+-]*@(?:[A-Za-z]{2,}\.)+[A-Za-z]{2,}$/,
        "Please enter a valid Email Address"
      ),
    instituteName: Yup.string().required(`Please select ${getLabel("INSTITUTE")}`),
    // role: Yup.string().required("Role is required"),
    status: Yup.string().required("Please select status"),
    address: Yup.string()
    .required("Please enter the address")
    .trim()
    .min(10, "Address must be at least 10 characters long.")
    .max(100, "Address must not exceed 100 characters"),
    city: Yup.string()
      .required("Please enter the city")
      .matches(/^[A-Za-z]+(?: [A-Za-z]+)*$/,"City name can only contain letters")
      .min(2, "City must be at least 2 characters")
      .max(60, "City must not exceed 60 characters"),
    state: Yup.string()
      .trim()
      .required("Please enter the state")
      .matches(/^[A-Za-z][A-Za-z -]*$/, "State can only contain letters, spaces or hyphens.")
      .min(3, "State must be at least 3 characters long.")
      .max(50, "State cannot exceed 50 characters."),
    pincode: Yup.string()
      .matches(/^[1-9][0-9]{5}$/, "Please enter a valid 6-digit pincode")
      .required("Please enter the pincode"),
    gender: Yup.string().required("Please select the gender"),
    dob: Yup.date()
      .required("Please enter your date of birth")
      .max(new Date(), "DOB cannot be in the future")
      .test(
        "min-age",
        "Age must be at least 18 years",
        (value) => !value || !dayjs(value).isAfter(dayjs().subtract(18, "year"), "day")
      )
      .typeError("Please enter a valid date"),
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      contactNumber: "",
      email: "",
      instituteName: "",
      //   role: "",
      status: "ACTIVE",
      address: "",
      city: "",
      state: "",
      pincode: "",
      gender: "Male",
      dob: "",
    },
  });

  // ===== API ======

  const instituteDropDown = async () => {
    try {
      const { data } = await api.post("ListInstituteNames");
      setInstituteNames(data?.data ?? []);
    } catch (error) {
      console.error("Error fetching institutes:", error);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        email: data?.email,
        dob: dayjs(data?.dob).format("YYYY-MM-DD"),
        last_name: data?.lastName,
        first_name: data?.firstName,
        gender: data?.gender,
        phone: data?.contactNumber,
        city: data?.city,
        state: data?.state,
        address: data?.address,
        pincode: data?.pincode,
        status: "ACTIVE",
        institute_id: +data?.instituteName,
      };

      const updatePayload = {
        user_id: adminID,
        email: data?.email,
        dob: dayjs(data?.dob).format("YYYY-MM-DD"),
        last_name: data?.lastName,
        first_name: data?.firstName,
        gender: data?.gender,
        phone: data?.contactNumber,
        city: data?.city,
        state: data?.state,
        address: data?.address,
        pincode: data?.pincode,
        status: data?.status,
        institute_id: +data?.instituteName,
      };
      if (adminID !== null) {
        const res = await api.post("UpdateInstituteAdminByID", updatePayload);
        if (res?.data?.status === "success") {
          navigate("/instituteAdmin");
          showSnackbar("success", res?.data?.message);
          setLoading(false);
        } else {
          showSnackbar("error", res?.data?.message);
          setLoading(false);
        }
      } else {
        const res = await api.post("AddInstituteAdmin", payload);
        if (res?.data?.status === "success") {
          navigate("/instituteAdmin");
          showSnackbar("success", (res?.data?.message || "").replace(/\bInstitute Admin\b/g, getLabel("INSTITUTE_ADMIN")));
          setLoading(false);
        } else {
          showSnackbar("error", res?.data?.message);
          setLoading(false);
        }
      }
    } catch (error) {
      console.log("Error :", error);
      setLoading(false);
    }
  };

  const fetchById = async () => {
    setOverAllLoading(true);
    try {
      const res = await api.post("ViewInstituteAdminByID", {
        user_id: adminID,
      });
      if (res?.data?.status === "success") {
        reset({
          firstName: res.data.data.first_name,
          lastName: res.data.data.last_name,
          contactNumber: res.data.data.phone,
          email: res.data.data.email,
          instituteName: res.data.data.institute_id,
          status: res.data.data.status,
          address: res.data.data.address,
          city: res.data.data.city,
          state: res.data.data.state,
          pincode: res.data.data.pincode,
          gender: res.data.data.gender,
          dob: res.data.data.dob,
        });
      }
      setOverAllLoading(false);
    } catch (error) {
      console.error("Error fetching admin by ID:", error);
      setOverAllLoading(false);
    }
  };

  // ==== UseEffect ====

  useEffect(() => {
    instituteDropDown();
    if (adminID !== null) {
      fetchById();
    }
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {overAllLoading ? (
        <OverAllLoader message={`Saving ${getLabel("INSTITUTE_ADMIN")}...`} />
      ) : (
        <Box>
          {/* Back Button */}
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#1c97e3",
              gap: "4px",
              cursor: "pointer",
              width: "40px",
              marginBottom: "20px",
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.history.back();
              }
            }}
            onClick={() => window.history.back()}
          >
            <ArrowBackIcon sx={{ fontSize: "17px" }} />
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}
            >
              Back
            </Typography>
          </Grid>

          {/* Form Card */}
          <Box>
            {/* Header */}
            <Grid
              sx={{
                pb: 2,
                display: "flex",
                gap: 1,
                alignItems: "center",
                borderBottom: "1px solid #d1d1d1",
              }}
            >
              <Grid
                sx={{
                  backgroundColor: "#1c97e3",
                  p: 0.5,
                  border: "1px",
                  borderRadius: "10px",
                }}
              >
                <AdminPanelSettingsIcon
                  sx={{ color: "white", fontSize: "35px" }}
                />
              </Grid>
              <Grid>
                <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
                  {addAdmin === "true" ? "Add" : "Edit"} {getLabel("INSTITUTE_ADMIN")}
                </Typography>
              </Grid>
            </Grid>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 2,
                  p: 3,
                }}
              >
                {/* Basic Info */}
                <Grid
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Grid>
                    <AdminPanelSettingsIcon
                      sx={{
                        color: "#1c97e3",
                        fontSize: "30px",
                        fontWeight: "bold",
                      }}
                    />
                  </Grid>
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                    Basic Information
                  </Typography>
                </Grid>
                <Grid sx={{ display: "flex", gap: 3 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      First Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="First Name"
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Last Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Last Name"
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid sx={{ display: "flex", gap: 3 }}>
                  {/* Gender Dropdown */}
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Gender <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="gender"
                      control={control}
                      rules={{ required: "Gender is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          fullWidth
                          placeholder="Select Gender"
                          error={!!errors.gender}
                          helperText={errors.gender?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        >
                          <MenuItem value="default" disabled>
                            Select Gender
                          </MenuItem>
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>

                  {/* Date of Birth */}
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Date of Birth <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="dob"
                      control={control}
                      rules={{ required: "Date of Birth is required" }}
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            {...field}
                            format="DD-MM-YYYY"
                            // Grey out dates that make the admin younger than 18
                            // maxDate={dayjs().subtract(18, "year")}
                            value={
                              field.value
                                ? dayjs.isDayjs(field.value)
                                  ? field.value
                                  : dayjs(field.value)
                                : null
                            }
                            // ✅ Keep as dayjs during typing to prevent refresh
                            onChange={(newValue) => field.onChange(newValue)}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.dob,
                                helperText: errors.dob?.message,
                                placeholder: "DD-MM-YYYY",
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": { borderColor: "white" },
                                    "&:hover fieldset": { borderColor: "#d1d1d1" },
                                    "&.Mui-focused fieldset": { borderColor: "#1c97e3" },
                                  },
                                  "& .MuiPickersOutlinedInput-notchedOutline": { borderColor: "white" },
                                  "&:hover .MuiPickersOutlinedInput-notchedOutline": { borderColor: "#d1d1d1" },
                                  "& .Mui-focused .MuiPickersOutlinedInput-notchedOutline": { borderColor: "#1c97e3" },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </Grid>
                </Grid>

                {addAdmin !== "true" && (
                  <Grid sx={{ display: "flex", gap: 3 }}>
                    {/* Status */}
                    <Grid sx={{ width: "49%" }}>
                      <Typography>
                        Status <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            fullWidth
                            error={!!errors.status}
                            helperText={errors.status?.message}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "white",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#d1d1d1",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1c97e3",
                                },
                              },
                            }}
                          >
                            {/* Placeholder */}
                            <MenuItem
                              value="default"
                              disabled
                              sx={{ color: "grey" }}
                            >
                              Select Status
                            </MenuItem>

                            {/* Options */}
                            {statuses.map((status) => (
                              <MenuItem key={status} value={status}>
                                {status.charAt(0).toUpperCase() +
                                  status.slice(1)}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>
                  </Grid>
                )}

                {/* Contact Info */}
                <Grid
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Grid>
                    <MailOutlineIcon
                      sx={{
                        color: "#1c97e3",
                        fontSize: "30px",
                        fontWeight: "bold",
                      }}
                    />
                  </Grid>
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                    Contact Information
                  </Typography>
                </Grid>
                <Grid sx={{ display: "flex", gap: 3 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Contact Number <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="contactNumber"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          placeholder="Contact Number"
                          fullWidth
                          onInput={(e) => {
                            e.target.value = e.target.value.replace(/\D/g, "");
                            field.onChange(e);
                          }}
                          error={!!errors.contactNumber}
                          helperText={errors.contactNumber?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                              "& input[type=number]": {
                                MozAppearance: "textfield",
                              },
                              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                              {
                                WebkitAppearance: "none",
                                margin: 0,
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Email <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder="Email"
                          fullWidth
                          error={!!errors.email}
                          helperText={errors.email?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Institute Info */}
                <Grid
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Grid>
                    <BusinessIcon
                      sx={{
                        color: "#1c97e3",
                        fontSize: "30px",
                        fontWeight: "bold",
                      }}
                    />
                  </Grid>
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                    {getLabel("INSTITUTE")} Information
                  </Typography>
                </Grid>
                <Grid sx={{ display: "flex", gap: 3 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      {getLabel("INSTITUTE")} Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="instituteName"
                      control={control}
                      render={({ field }) => {
                        const currentValue =
                          instituteNames.find(
                            (inst) => inst.institute_id === field.value
                          ) || null;

                        return (
                          <Autocomplete
                            options={instituteNames}
                            getOptionLabel={(option) => option.name}
                            value={currentValue}
                            onChange={(_, value) =>
                              field.onChange(value ? value.institute_id : "")
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.institute_id === value.institute_id
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={`Select ${getLabel("INSTITUTE")}`}
                                fullWidth
                                error={!!errors.instituteName}
                                helperText={errors.instituteName?.message}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "white",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "#d1d1d1",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#1c97e3",
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                        );
                      }}
                    />
                  </Grid>

                  {/* <Grid sx={{ width: "100%" }}>
                  <Typography>
                    Role <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        placeholder="Select Role"
                        error={!!errors.role}
                        helperText={errors.role?.message}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "white",
                            },
                            "&:hover fieldset": {
                              borderColor: "#d1d1d1",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#1c97e3",
                            },
                          },
                        }}
                      >
                        {roles.map((role) => (
                          <MenuItem key={role} value={role}>
                            {role}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid> */}
                </Grid>
                {/* Address Info */}
                <Grid
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Grid>
                    <LocationOnIcon
                      sx={{
                        color: "#1c97e3",
                        fontSize: "30px",
                        fontWeight: "bold",
                      }}
                    />
                  </Grid>
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                    Address Information
                  </Typography>
                </Grid>
                <Typography>
                  Address <span style={{ color: "red" }}>*</span>
                </Typography>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Address"
                      multiline
                      minRows={3}
                      error={!!errors.address}
                      helperText={errors.address?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "white",
                          },
                          "&:hover fieldset": {
                            borderColor: "#d1d1d1",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#1c97e3",
                          },
                        },
                      }}
                    />
                  )}
                />

                <Grid sx={{ display: "flex", gap: 3 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      City <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="city"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="City"
                          error={!!errors.city}
                          helperText={errors.city?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      State <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="state"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="State"
                          error={!!errors.state}
                          helperText={errors.state?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Pincode <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="pincode"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Pincode"
                          inputProps={{ maxLength: 6 }}
                          error={!!errors.pincode}
                          helperText={errors.pincode?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 3,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate(-1)}
                    sx={{
                      textTransform: "none",
                      borderRadius: "12px",
                      px: 3,
                      py: 1,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: "none",
                      borderRadius: "12px",
                      px: 4,
                      py: 1,
                      fontWeight: 600,
                    }}
                    disabled={loading}
                  >
                    {addAdmin === "true" ? "Add" : "Update"} Admin
                  </Button>
                </Box>
              </Box>
            </form>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AddInstituteAdmin;
